import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Stack, Paper, Divider, Grid, Chip} from 'views/components';
import {Translate, translate, MarkdownEditor} from 'views/module';
import {Info} from 'views/icons';

const Consideration = (props) => {
  const {newJob, updateJobConsiderations, setIsStepValid} = props;

  const considerations = newJob.considerations || {};
  const selectedOptions = considerations.options || [];
  const additionalConsiderations = considerations.additionalConsiderations?.blob || '';

  // Considerations options array - get keys from translation object
  const considerationOptionsObject = translate('career.create.Considerations.Options');
  const considerationOptions = Object.keys(considerationOptionsObject);

  React.useEffect(() => {
    // This step is always valid - considerations are optional
    setIsStepValid(true);
  }, [setIsStepValid]);

  const handleOptionToggle = (option) => {
    updateJobConsiderations((considerations) => {
      const currentOptions = considerations.options || [];
      const isSelected = currentOptions.includes(option);

      const updatedOptions = isSelected ?
        currentOptions.filter((opt) => opt !== option) :
        [...currentOptions, option];

      return {
        ...considerations,
        options: updatedOptions,
      };
    });
  };

  const handleAdditionalConsiderationsChange = (event) => {
    const value = event;
    updateJobConsiderations((considerations) => ({
      ...considerations,
      additionalConsiderations: {
        blob: value,
        editorType: 'MARKDOWN',
      },
    }));
  };

  const getSelectedDescriptions = () => {
    return selectedOptions.map((option) => ({
      key: option,
      label: translate(`career.create.Considerations.Options.${option}`),
    }));
  };

  return (
    <Box sx={{p: 2, maxWidth: 1200, mx: 'auto'}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.Considerations.Title" />
      </Typography>

      {/* Common Considerations Selection */}
      <Box sx={{mb: 4, textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
          <Translate expression="career.create.Considerations.CommonConsiderationsLabel" />
        </Typography>

        {/* Considerations Options Grid */}
        <Grid container spacing={1.5} justifyContent="center">
          {
            considerationOptions.map((option) => {
              const isSelected = selectedOptions.includes(option);
              const optionLabel = translate(`career.create.Considerations.Options.${option}`);

              return (
                <Grid item key={option}>
                  <Chip
                    label={optionLabel}
                    onClick={() => handleOptionToggle(option)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                  />
                </Grid>
              );
            })
          }
        </Grid>
      </Box>

      {/* Additional Considerations Text Area */}
      <Box sx={{mb: 4, textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
          <Translate expression="career.create.Considerations.AdditionalConsiderationsLabel" />
        </Typography>
        <Box sx={{maxWidth: 900, mx: 'auto'}}>
          <MarkdownEditor
            value={additionalConsiderations}
            onChange={handleAdditionalConsiderationsChange}
            height={150}
          />
        </Box>
      </Box>

      {/* Considerations Preview Summary */}
      {
        (selectedOptions.length > 0 || additionalConsiderations.trim()) &&
        <Box sx={{maxWidth: 900, mx: 'auto'}}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#fdf2f8',
              borderRadius: 2,
              border: '1px solid #fce7f3',
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Info sx={{color: '#ec4899', mt: 0.5}} />
              <Box sx={{width: '100%'}}>
                {/* Selected Options */}
                {
                  selectedOptions.length > 0 &&
                    <Box sx={{mb: additionalConsiderations.trim() ? 2 : 0}}>
                      <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#ec4899', mb: 1}}>
                        <Translate expression="career.create.Considerations.SelectedLabel" />:
                      </Typography>
                      <Box>
                        {
                          getSelectedDescriptions().map((item, index) => (
                            <Typography key={item.key} variant="body2" sx={{mb: 0.5}}>
                              {item.label}
                            </Typography>
                          ))
                        }
                      </Box>
                    </Box>
                }

                {/* Additional Considerations */}
                {
                  additionalConsiderations.trim() &&
                    <Box>
                      {selectedOptions.length > 0 && <Divider sx={{my: 2}} />}
                      <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#ec4899', mb: 1}}>
                        <Translate expression="career.create.Considerations.AdditionalConsiderationsLabel" />:
                      </Typography>
                      <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                        {additionalConsiderations}
                      </Typography>
                    </Box>
                }
              </Box>
            </Stack>
          </Paper>
        </Box>
      }

      {/* Empty State */}
      {
        selectedOptions.length === 0 && !additionalConsiderations.trim() &&
          <Box sx={{maxWidth: 900, mx: 'auto'}}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: '#f9fafb',
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <Translate expression="career.create.Considerations.NoConsiderationsSelected" />
              </Typography>
            </Paper>
          </Box>
      }
    </Box>
  );
};

Consideration.propTypes = {
  newJob: PropTypes.object.isRequired,
  updateJobConsiderations: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
};

export default Consideration;
